<script setup>
import { ref, onMounted, computed, watch, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { dt } from '@/utils/i18n'

import {
  NAvatar, NButton, NCard, NTag, NSpin, NAlert, NSpace, NRate, NDivider,
  NEmpty, NTable, NCollapse, NCollapseItem, NTabs, NTabPane
} from 'naive-ui';
import { getProvider, getProviderProfileImage, getProviderPhoneNumbers, getProviderImages } from '@/services/provider.js';
import { fetchProviderReviews, fetchMyReviewForProvider, fetchProviderReviewSummary, hasReviewedProvider } from '@/services/review.js';
import BookmarkButton from '@/components/BookmarkButton.vue';
import { authToken, isAuthenticated } from "@/services/auth";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const provider = ref(null);
const loading = ref(true);
const error = ref(null);

// Review-related refs
const reviewSummary = ref(null);
const recentReviews = ref([]);
const loadingReviews = ref(false);
const hasUserReviewed = ref(false);

// Phone visibility states
const profileImageUrl = ref(null);
const showPhones = ref(false);
const loadingPhones = ref(false);
const phoneNumbers = ref([]);

// Office images state
const facilityImages = ref([]);
const loadingImages = ref(false);

// Map state
const mapContainer = ref(null);
const mapInitialized = ref(false);
const mapError = ref(null);
let map = null;
let marker = null;

// Active tab for mobile view
const activeTab = ref('basic');

// Track the selected language
const selectedLanguage = ref(localStorage.getItem('preferredLanguage') || 'english');

const fetchProvider = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Use the reactive selectedLanguage value
    const language = selectedLanguage.value;

    // Use the service function with language parameter
    const data = await getProvider(route.params.id, language);
    try {
      const imageResult = await getProviderProfileImage(data.id);
      if (imageResult.success && imageResult.image_url) {
        profileImageUrl.value = imageResult.image_url;
      }
    } catch (imageError) {
      console.error('Error loading profile image:', imageError);
      // Don't set main error, just log it since the profile loaded successfully
    }

    provider.value = data;

    // After provider is loaded, fetch reviews data
    fetchReviewData();

    // Load office images if available
    if (data && data.id) {
      loadFacilityImages(data.id);
    }

    // Initialize map if provider has coordinates
    if (data && data.latitude && data.longitude) {
      console.log('Provider has coordinates, scheduling map initialization...');
      await nextTick();
      // Delay to ensure DOM is ready
      setTimeout(() => {
        initializeMap();
      }, 1000);
    }
  } catch (err) {
    console.error('Error fetching provider:', err);
    error.value = dt('providerDetail.errors.failedToLoad', { message: err.message });
  } finally {
    loading.value = false;
  }
};

// Watch for tab changes to initialize map when needed
watch(activeTab, async (newTab) => {
  if (newTab === 'basic' && provider.value && provider.value.latitude && provider.value.longitude && !mapInitialized.value) {
    console.log('Basic tab activated, initializing map...');
    await nextTick();
    setTimeout(() => {
      initializeMap();
    }, 500);
  }
});

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Map utility functions
const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      console.log('Leaflet already loaded');
      resolve(window.L);
      return;
    }

    console.log('Loading Leaflet...');

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlefM=';
    script.crossOrigin = '';
    
    script.onload = () => {
      console.log('Leaflet loaded successfully');
      if (window.L) {
        resolve(window.L);
      } else {
        reject(new Error('Failed to load Leaflet'));
      }
    };
    
    script.onerror = () => {
      console.error('Failed to load Leaflet');
      reject(new Error('Failed to load Leaflet'));
    };
    
    document.head.appendChild(script);
  });
};

const initializeMap = async () => {
  try {
    console.log('Starting map initialization...');
    
    if (mapInitialized.value) {
      console.log('Map already initialized');
      return;
    }

    if (!provider.value || !provider.value.latitude || !provider.value.longitude) {
      console.log('No coordinates available for map');
      return;
    }

    // Check if map container exists and is visible
    const container = mapContainer.value;
    if (!container) {
      console.error('Map container element not found');
      mapError.value = ddt('providerDetail.map.containerNotFound');
      return;
    }

    // Check if container has dimensions
    const rect = container.getBoundingClientRect();
    console.log('Container dimensions:', rect);
    
    if (rect.width === 0 || rect.height === 0) {
      console.error('Map container has no dimensions, retrying...');
      // Try again after a longer delay
      setTimeout(() => {
        if (!mapInitialized.value) {
          initializeMap();
        }
      }, 1000);
      return;
    }

    console.log('Loading Leaflet...');
    await loadLeaflet();
    const L = window.L;

    const lat = parseFloat(provider.value.latitude);
    const lng = parseFloat(provider.value.longitude);

    if (isNaN(lat) || isNaN(lng)) {
      console.log('Invalid coordinates');
      mapError.value = dt('providerDetail.map.invalidCoordinates');
      return;
    }

    console.log(`Creating map at coordinates: ${lat}, ${lng}`);

    // Clear any existing map
    if (map) {
      map.remove();
      map = null;
      marker = null;
    }

    // Initialize the map
    map = L.map(container, {
      zoomControl: true,
      scrollWheelZoom: true
    }).setView([lat, lng], 15);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    // Add marker for the provider
    const markerIcon = L.divIcon({
      html: `<div style="
        background-color: #3b82f6;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      "></div>`,
      className: 'provider-marker',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);

    // Add popup with provider information
    const popupContent = `
      <div style="min-width: 200px; text-align: center;">
        <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${provider.value.provider_name || dt('providerDetail.unknownProvider')}</h4>
        <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
          ${provider.value.provider_type || dt('providerDetail.legalServicesProvider')}
        </p>
        <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
          ${provider.value.city || ''}${provider.value.city && provider.value.state ? ', ' : ''}${provider.value.state || ''}
        </p>
        <div style="margin-top: 10px;">
          <button onclick="window.open('https://maps.google.com?q=${lat},${lng}', '_blank')" style="
            background: #10b981; 
            color: white; 
            border: none; 
            padding: 6px 12px; 
            border-radius: 4px; 
            cursor: pointer; 
            font-size: 12px;
          ">${dt('providerDetail.map.getDirections')}</button>
        </div>
      </div>
    `;

    marker.bindPopup(popupContent);

    mapInitialized.value = true;
    mapError.value = null;
    console.log('Map initialized successfully');

    // Force map to resize after initialization
    setTimeout(() => {
      if (map) {
        map.invalidateSize();
        console.log('Map size invalidated');
      }
    }, 100);

  } catch (error) {
    console.error('Error initializing map:', error);
    mapError.value = dt('providerDetail.map.failedToLoad', { message: error.message });
  }
};

// Load office images
async function loadFacilityImages(providerId) {
  try {
    loadingImages.value = true;
    const result = await getProviderImages(providerId);

    if (result.success && result.images) {
      facilityImages.value = result.images;
    }
  } catch (err) {
    console.error('Failed to load office images:', err);
  } finally {
    loadingImages.value = false;
  }
}

const fetchReviewData = async () => {
  if (!provider.value) return;

  loadingReviews.value = true;

  try {
    // Fetch review summary
    reviewSummary.value = await fetchProviderReviewSummary(route.params.id);

    // Fetch recent reviews (limited to 3)
    const response = await fetchProviderReviews(route.params.id);
    recentReviews.value = response.reviews.slice(0, 3);

    // Check if the user has already reviewed this provider
    if (isAuthenticated.value) {
      hasUserReviewed.value = await hasReviewedProvider(authToken.value, route.params.id);
    }
  } catch (err) {
    console.error('Error fetching reviews:', err);
    // Don't set a global error - we still want to show the provider details
  } finally {
    loadingReviews.value = false;
  }
};

// Handler for language-changed event
const handleLanguageChange = (event) => {
  const newLanguage = event.detail.language || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;
    fetchProvider();
  }
};

// Handler for app-language-updated event (backup)
const handleAppLanguageUpdate = (event) => {
  const newLanguage = localStorage.getItem("preferredLanguage") || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;
    fetchProvider();
  }
};

// Watch for changes to the language in localStorage
watch(
  () => localStorage.getItem("preferredLanguage"),
  (newLanguage, oldLanguage) => {
    if (newLanguage && newLanguage !== oldLanguage && newLanguage !== selectedLanguage.value) {
      selectedLanguage.value = newLanguage;
      fetchProvider();
    }
  }
);

// Format office hours for display
function formatOfficeHours(officeHours) {
  if (!officeHours || typeof officeHours !== 'object') return [];

  // Convert HashMap to array format
  const hoursArray = [];

  Object.keys(officeHours).forEach(day => {
    const timeSlots = officeHours[day];
    timeSlots.forEach(slot => {
      hoursArray.push({
        day: day,
        open: slot.open,
        close: slot.close
      });
    });
  });

  // Sort by day of week
  const dayOrder = {
    'Monday': 0,
    'Tuesday': 1,
    'Wednesday': 2,
    'Thursday': 3,
    'Friday': 4,
    'Saturday': 5,
    'Sunday': 6
  };

  return hoursArray.sort((a, b) => {
    return dayOrder[a.day] - dayOrder[b.day];
  });
}

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const navigateToReviewForm = () => {
  router.push(`/providers/${route.params.id}/review/create`);
};

const navigateToReviewUpdateForm = async () => {
  try {
    const myReview = await fetchMyReviewForProvider(authToken.value, route.params.id);
    if (myReview && myReview.id) {
      router.push(`/providers/${route.params.id}/review/${myReview.id}`);
    } else {
      router.push(`/providers/${route.params.id}/review/create`);
    }
  } catch (error) {
    console.error("Error fetching review for navigation:", error);
    router.push(`/providers/${route.params.id}/review/create`);
  }
};

const navigateToAllReviews = () => {
  router.push(`/providers/${route.params.id}/reviews`);
};

const loadPhoneNumbers = async () => {
  if (loadingPhones.value) return;

  loadingPhones.value = true;
  try {
    const result = await getProviderPhoneNumbers(route.params.id);
    phoneNumbers.value = result.phone_numbers;
    showPhones.value = true;
  } catch (error) {
    console.error('Failed to load phone numbers:', error);
  } finally {
    loadingPhones.value = false;
  }
};

const onBookmarkStatusChange = (newStatus) => {
  // Additional logic if needed
};

const goBack = () => {
  router.back();
};

const goToProviders = () => {
  router.push('/providers');
};

// Status translation function
const translateStatus = (status) => {
  const statusKey = `providerDetail.status.${status}`;
  const translated = t(statusKey);
  return translated !== statusKey ? translated : status;
};

// Computed property for status class
const statusClass = computed(() => {
  if (!provider.value || !provider.value.status) return '';
  return `status-${provider.value.status.toLowerCase()}`;
});

// Check if provider has location coordinates
const hasLocation = computed(() => {
  return provider.value && 
         provider.value.latitude && 
         provider.value.longitude && 
         !isNaN(parseFloat(provider.value.latitude)) && 
         !isNaN(parseFloat(provider.value.longitude));
});

// Check if professional details section should be shown
const showProfessionalDetails = computed(() => {
  if (!provider.value) return false;

  return !!(
    (provider.value.services && provider.value.services.length > 0) ||
    (provider.value.education && provider.value.education.length > 0) ||
    (provider.value.certifications && provider.value.certifications.length > 0) ||
    (provider.value.spoken_languages && provider.value.spoken_languages.length > 0) ||
    provider.value.years_of_experience
  );
});

// Check if practice information section should be shown
const showPracticeInfo = computed(() => {
  if (!provider.value) return false;

  return !!(
    provider.value.accepts_new_clients !== null ||
    provider.value.virtual_consultations !== null ||
    (provider.value.office_hours && provider.value.office_hours.length > 0) ||
    provider.value.parking_info ||
    (provider.value.payment_methods && provider.value.payment_methods.length > 0) ||
    (provider.value.accessibility_options && provider.value.accessibility_options.length > 0) ||
    provider.value.average_response_time_minutes !== null
  );
});

// Check if legal practice details section should be shown
const showLegalPracticeDetails = computed(() => {
  if (!provider.value) return false;

  return !!(
    (provider.value.court_representations && provider.value.court_representations.length > 0) ||
    (provider.value.case_types_handled && provider.value.case_types_handled.length > 0) ||
    (provider.value.pro_bono_services && provider.value.pro_bono_services.length > 0) ||
    (provider.value.affiliated_organizations && provider.value.affiliated_organizations.length > 0) ||
    (provider.value.technology_available && provider.value.technology_available.length > 0) ||
    (provider.value.awards_accreditations && provider.value.awards_accreditations.length > 0) ||
    (provider.value.provider_staff && provider.value.provider_staff.length > 0) ||
    (provider.value.services_offered && provider.value.services_offered.length > 0)
  );
});

onMounted(() => {
  fetchProvider();
  window.addEventListener('language-changed', handleLanguageChange);
  window.addEventListener('app-language-updated', handleAppLanguageUpdate);
});

onUnmounted(() => {
  window.removeEventListener('language-changed', handleLanguageChange);
  window.removeEventListener('app-language-updated', handleAppLanguageUpdate);
  if (map) {
    map.remove();
    map = null;
  }
});
</script>

<template>
  <div class="provider-detail-page">
    <main class="main-content">
      <div class="container">
        <!-- Back Button -->
        <div class="back-section">
          <NButton @click="goBack" class="back-btn">
            {{ dt('providerDetail.backButton') }}
          </NButton>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-section">
          <NSpin size="large">
            <div class="loading-text">{{ dt('providerDetail.loading') }}</div>
          </NSpin>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-section">
          <NAlert type="error" :title="dt('providerDetail.errorTitle')" closable>
            {{ error }}
            <template #action>
              <NButton size="small" @click="fetchProvider">
                {{ dt('providerDetail.tryAgain') }}
              </NButton>
            </template>
          </NAlert>
        </div>

        <!-- Provider Details -->
        <div v-else-if="provider" class="provider-details">
          <!-- Provider Header -->
          <div class="provider-header">
            <div class="provider-main">
              <NAvatar round size="large" :src="profileImageUrl" class="profile-avatar">
                <template v-if="!profileImageUrl">
                  {{ getInitials(provider.provider_name) }}
                </template>
              </NAvatar>

              <div class="provider-info">
                <h1 class="provider-name">{{ provider.provider_name || dt('providerDetail.unknownProvider') }}</h1>
                <div v-if="provider.provider_type" class="provider-type">{{ provider.provider_type }}</div>

                <div class="provider-status" v-if="provider && provider.status">
                  <span class="status-badge" :class="statusClass">
                    {{ translateStatus(provider.status) }}
                  </span>
                  <span v-if="provider.user_name" class="provider-username">
                    {{ dt('providerDetail.provider') }}: {{ provider.user_name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Claim Notice -->
          <div v-if="provider && provider.status === 'unclaimed'" class="claim-notice">
            <div class="notice-title">{{ dt('providerDetail.claim.areYouThisProvider') }}</div>
            <div class="notice-text">{{ dt('providerDetail.claim.description') }}</div>
            <NButton type="warning" @click="router.push(`/providers/${route.params.id}/claim`)">
              {{ dt('providerDetail.claim.claimButton') }}
            </NButton>
          </div>

          <!-- Mobile Tabs View -->
          <div class="mobile-tabs">
            <NTabs type="line" animated v-model:value="activeTab">
              <NTabPane name="basic" :tab="dt('providerDetail.tabs.basicInfo')">
                <!-- Basic Information -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.basicInformation') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.city">
                      <div class="info-label">{{ dt('providerDetail.fields.location') }}:</div>
                      <div class="info-value">{{ provider.city }}</div>
                    </div>
                    <div class="info-item" v-if="provider.specializations?.length">
                      <div class="info-label">{{ dt('providerDetail.fields.practiceAreas') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="spec in provider.specializations" :key="spec" type="primary" class="spec-tag">
                          {{ spec }}
                        </NTag>
                      </div>
                    </div>
                    <div class="info-item full-width" v-if="provider.additional_info">
                      <div class="info-label">{{ dt('providerDetail.fields.about') }}:</div>
                      <div class="info-value">{{ provider.additional_info }}</div>
                    </div>
                    <div class="info-item" v-if="provider.tags?.length">
                      <div class="info-label">{{ dt('providerDetail.fields.tags') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="tag in provider.tags" :key="tag" type="success" class="tag-item">
                          {{ tag }}
                        </NTag>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Contact Information -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.contactInformation') }}</h2>
                  <div class="info-grid">
                    <!-- Phone Numbers -->
                    <div class="info-item full-width">
                      <div class="info-label">{{ dt('providerDetail.fields.phone') }}:</div>
                      <div class="info-value">
                        <div class="phone-section">
                          <div v-if="!showPhones">
                            <button class="phone-button" @click="loadPhoneNumbers" :disabled="loadingPhones">
                              <span v-if="loadingPhones" class="loading-dots">
                                {{ dt('providerDetail.phone.loading') }}<span>.</span><span>.</span><span>.</span>
                              </span>
                              <span v-else>
                                <span class="phone-icon">📞</span>
                                <span class="phone-text">{{ dt('providerDetail.phone.showPhoneNumbers') }}</span>
                              </span>
                            </button>
                          </div>

                          <div v-else-if="phoneNumbers.length > 0" class="phone-list">
                            <div v-for="(phone, index) in phoneNumbers" :key="index" class="phone-item">
                              <a :href="`tel:${phone}`" class="phone-link">
                                {{ phone }}
                              </a>
                            </div>
                          </div>

                          <div v-else-if="showPhones" class="no-phones">
                            {{ dt('providerDetail.phone.noPhoneNumbers') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Email -->
                    <div class="info-item" v-if="provider.email">
                      <div class="info-label">{{ dt('providerDetail.fields.email') }}:</div>
                      <div class="info-value">
                        <a :href="`mailto:${provider.email}`" class="contact-link">
                          {{ provider.email }}
                        </a>
                      </div>
                    </div>

                    <!-- Website -->
                    <div class="info-item" v-if="provider.website_url">
                      <div class="info-label">{{ dt('providerDetail.fields.website') }}:</div>
                      <div class="info-value">
                        <a :href="provider.website_url" target="_blank" class="contact-link">
                          {{ provider.website_url }}
                        </a>
                      </div>
                    </div>

                    <!-- Address -->
                    <div class="info-item full-width" v-if="provider.address_line1 || provider.address_line2 || provider.address_line3">
                      <div class="info-label">{{ dt('providerDetail.fields.address') }}:</div>
                      <div class="info-value address-block">
                        <p v-if="provider.address_line1">{{ provider.address_line1 }}</p>
                        <p v-if="provider.address_line2">{{ provider.address_line2 }}</p>
                        <p v-if="provider.address_line3">{{ provider.address_line3 }}</p>
                        <p>
                          {{ provider.city }}{{ provider.city && provider.state ? ', ' : '' }}{{ provider.state }}
                          {{ provider.postal_code ? provider.postal_code : '' }}
                        </p>
                      </div>
                    </div>

                    <!-- Social Media -->
                    <div class="info-item full-width" v-if="provider.social_media && provider.social_media.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.socialMedia') }}:</div>
                      <div class="info-value">
                        <div class="social-links">
                          <div v-for="(social, index) in provider.social_media" :key="index" class="social-item">
                            <strong>{{ social.platform }}:</strong>
                            <a :href="social.url.startsWith('http') ? social.url : 'https://' + social.url" target="_blank" class="contact-link">
                              {{ social.url }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Map Section for Mobile -->
                <section v-if="hasLocation" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.location') }}</h2>
                  <div v-if="mapError" class="map-error">
                    <div class="error-content">
                      <p>{{ mapError }}</p>
                      <NButton size="small" @click="initializeMap">
                        {{ dt('providerDetail.map.retryLoading') }}
                      </NButton>
                    </div>
                  </div>
                  <div v-else class="map-container-mobile">
                    <div ref="mapContainer" class="map"></div>
                  </div>
                </section>
              </NTabPane>

              <NTabPane name="professional" :tab="dt('providerDetail.tabs.professional')">
                <!-- Professional Details -->
                <section v-if="showProfessionalDetails" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.professionalDetails') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.years_of_experience">
                      <div class="info-label">{{ dt('providerDetail.fields.yearsOfExperience') }}:</div>
                      <div class="info-value">{{ provider.years_of_experience }}</div>
                    </div>

                    <div class="info-item full-width" v-if="provider.services && provider.services.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.legalServices') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="service in provider.services" :key="service" type="info" class="service-tag">
                          {{ service }}
                        </NTag>
                      </div>
                    </div>

                    <div class="info-item full-width" v-if="provider.spoken_languages && provider.spoken_languages.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.languages') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="lang in provider.spoken_languages" :key="lang" type="default" class="lang-tag">
                          {{ lang }}
                        </NTag>
                      </div>
                    </div>
                  </div>

                  <!-- Education -->
                  <div v-if="provider.education && provider.education.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.education') }}</h3>
                    <div class="education-list">
                      <div v-for="(edu, i) in provider.education" :key="i" class="education-item">
                        <div class="education-year">{{ edu.year }}</div>
                        <div class="education-details">
                          <div class="education-degree">{{ edu.degree }}</div>
                          <div class="education-institution">{{ edu.institution }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Certifications -->
                  <div v-if="provider.certifications && provider.certifications.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.certifications') }}</h3>
                    <div class="certification-list">
                      <div v-for="(cert, i) in provider.certifications" :key="i" class="certification-item">
                        <div class="certification-year">{{ cert.year }}</div>
                        <div class="certification-details">
                          <div class="certification-name">{{ cert.name }}</div>
                          <div class="certification-issuer">{{ cert.issuer }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div v-else class="empty-section">
                  <NEmpty :description="dt('providerDetail.empty.noProfessionalDetails')" />
                </div>
              </NTabPane>

              <NTabPane name="legal" :tab="dt('providerDetail.tabs.legalPractice')">
                <!-- Legal Practice Details -->
                <section v-if="showLegalPracticeDetails" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.legalPracticeDetails') }}</h2>

                  <div v-if="provider.court_representations && provider.court_representations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.courtRepresentations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="court in provider.court_representations" :key="court" type="primary" class="court-tag">
                        {{ court }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.case_types_handled && provider.case_types_handled.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.caseTypesHandled') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="caseType in provider.case_types_handled" :key="caseType" type="info" class="case-tag">
                        {{ caseType }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.pro_bono_services && provider.pro_bono_services.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.proBonoServices') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="service in provider.pro_bono_services" :key="service" type="success" class="pro-bono-tag">
                        {{ service }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.affiliated_organizations && provider.affiliated_organizations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.barAssociationsOrganizations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="org in provider.affiliated_organizations" :key="org" type="warning" class="org-tag">
                        {{ org }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.technology_available && provider.technology_available.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.legalTechnologyAvailable') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="tech in provider.technology_available" :key="tech" type="info" class="tech-tag">
                        {{ tech }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.awards_accreditations && provider.awards_accreditations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.awardsAccreditations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="award in provider.awards_accreditations" :key="award" type="success" class="award-tag">
                        {{ award }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.provider_staff && provider.provider_staff.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.staffMembers') }}</h3>
                    <div class="staff-list">
                      <div v-for="(staff, i) in provider.provider_staff" :key="i" class="staff-item">
                        <div class="staff-name">{{ staff.name }}</div>
                        <div class="staff-role">{{ staff.role }}</div>
                        <div v-if="staff.qualifications" class="staff-qualifications">{{ staff.qualifications }}</div>
                      </div>
                    </div>
                  </div>

                  <div v-if="provider.services_offered && provider.services_offered.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.servicesOffered') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="service in provider.services_offered" :key="service" type="primary" class="detailed-service-tag">
                        {{ service }}
                      </NTag>
                    </div>
                  </div>
                </section>

                <div v-else class="empty-section">
                  <NEmpty :description="dt('providerDetail.empty.noLegalPracticeDetails')" />
                </div>
              </NTabPane>

              <NTabPane name="practice" :tab="dt('providerDetail.tabs.practice')">
                <!-- Practice Information -->
                <section v-if="showPracticeInfo" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.practiceInformation') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.accepts_new_clients !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.acceptingNewClients') }}:</div>
                      <div class="info-value">
                        <span :class="provider.accepts_new_clients ? 'status-positive' : 'status-negative'">
                          {{ provider.accepts_new_clients ? dt('providerDetail.common.yes') : dt('providerDetail.common.no') }}
                        </span>
                      </div>
                    </div>

                    <div class="info-item" v-if="provider.virtual_consultations !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.virtualConsultations') }}:</div>
                      <div class="info-value">
                        <span :class="provider.virtual_consultations ? 'status-positive' : 'status-negative'">
                          {{ provider.virtual_consultations ? dt('providerDetail.common.yes') : dt('providerDetail.common.no') }}
                        </span>
                      </div>
                    </div>

                    <div class="info-item" v-if="provider.average_response_time_minutes !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.averageResponseTime') }}:</div>
                      <div class="info-value">{{ provider.average_response_time_minutes }} {{ dt('providerDetail.common.hours') }}</div>
                    </div>
                  </div>

                  <!-- Office Hours -->
                  <div v-if="provider.office_hours && provider.office_hours.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.officeHours') }}</h3>
                    <div class="hours-container">
                      <div v-for="(hours, i) in formatOfficeHours(provider.office_hours)" :key="i" class="hours-item">
                        <div class="hours-day">{{ hours.day }}</div>
                        <div class="hours-time">{{ hours.open }} - {{ hours.close }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Parking Info -->
                  <div v-if="provider.parking_info" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.parkingInformation') }}</h3>
                    <div class="parking-info">{{ provider.parking_info }}</div>
                  </div>

                  <!-- Payment Methods -->
                  <div v-if="provider.payment_methods && provider.payment_methods.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.paymentMethods') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="method in provider.payment_methods" :key="method" type="warning" class="payment-tag">
                        {{ method }}
                      </NTag>
                    </div>
                  </div>

                  <!-- Accessibility Features -->
                  <div v-if="provider.accessibility_options && provider.accessibility_options.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.accessibilityFeatures') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="option in provider.accessibility_options" :key="option" type="success" class="access-tag">
                        {{ option }}
                      </NTag>
                    </div>
                  </div>
                </section>

                <div v-else class="empty-section">
                  <NEmpty :description="dt('providerDetail.empty.noPracticeInformation')" />
                </div>
              </NTabPane>

              <NTabPane name="clients" :tab="dt('providerDetail.tabs.clientTypes')">
                <!-- Client Types Information -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.clientTypesServed') }}</h2>

                  <div v-if="provider.client_types && provider.client_types.length > 0">
                    <div class="tags-container">
                      <NTag v-for="clientType in provider.client_types" :key="clientType" type="warning" class="client-tag">
                        {{ clientType }}
                      </NTag>
                    </div>
                  </div>

                  <div v-else class="empty-section">
                    <NEmpty :description="dt('providerDetail.empty.noClientTypeInformation')" />
                  </div>
                </section>
              </NTabPane>

              <NTabPane name="reviews" :tab="dt('providerDetail.tabs.reviews')">
                <!-- Reviews Section -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.ratingsReviews') }}</h2>

                  <div v-if="loadingReviews" class="rating-loading">
                    <NSpin size="small" />
                    <span>{{ dt('providerDetail.reviews.loading') }}</span>
                  </div>

                  <div v-else-if="reviewSummary" class="rating-summary">
                    <div class="overall-rating">
                      <div class="rating-number">{{ reviewSummary.average_rating.toFixed(1) }}</div>
                      <NRate readonly :value="reviewSummary.average_rating" allow-half />
                      <div class="review-count">{{ reviewSummary.review_count }} {{ dt('providerDetail.reviews.reviewsCount') }}</div>
                    </div>

                    <div class="detailed-ratings" v-if="reviewSummary.review_count > 0">
                      <div class="rating-item" v-if="reviewSummary.average_communication">
                        <span class="rating-label">{{ dt('providerDetail.reviews.communication') }}:</span>
                        <NRate readonly :value="reviewSummary.average_communication" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_communication.toFixed(1) }}</span>
                      </div>

                      <div class="rating-item" v-if="reviewSummary.average_knowledge">
                        <span class="rating-label">{{ dt('providerDetail.reviews.knowledge') }}:</span>
                        <NRate readonly :value="reviewSummary.average_knowledge" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_knowledge.toFixed(1) }}</span>
                      </div>

                      <div class="rating-item" v-if="reviewSummary.average_timeliness">
                        <span class="rating-label">{{ dt('providerDetail.reviews.timeliness') }}:</span>
                        <NRate readonly :value="reviewSummary.average_timeliness" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_timeliness.toFixed(1) }}</span>
                      </div>
                    </div>

                    <div class="review-actions">
                      <NButton type="info" @click="navigateToAllReviews" v-if="reviewSummary.review_count > 0">
                        {{ dt('providerDetail.reviews.seeAllReviews') }}
                      </NButton>

                      <NButton @click="navigateToReviewUpdateForm" v-if="isAuthenticated && hasUserReviewed">
                        {{ dt('providerDetail.reviews.editYourReview') }}
                      </NButton>

                      <div v-if="!isAuthenticated" class="login-prompt">
                        {{ dt('providerDetail.reviews.loginToReview') }}
                      </div>
                    </div>
                  </div>

                  <!-- Recent Reviews -->
                  <div v-if="recentReviews.length > 0" class="recent-reviews">
                    <h3 class="subsection-title">{{ dt('providerDetail.reviews.recentReviews') }}</h3>

                    <div v-for="(review, index) in recentReviews" :key="index" class="review-item">
                      <div class="review-header">
                        <div class="reviewer-info">
                          <div class="reviewer-name">{{ review.reviewer_name || dt('providerDetail.reviews.anonymous') }}</div>
                          <div class="review-date">{{ formatDate(review.created_at) }}</div>
                        </div>
                        <NRate readonly :value="review.rating" size="small" />
                      </div>

                      <div class="review-text" v-if="review.review_text">
                        <span class="truncated-text">{{ review.review_text }}</span>
                      </div>

                      <NDivider v-if="index < recentReviews.length - 1" />
                    </div>

                    <div class="see-all-reviews">
                      <NButton text @click="navigateToAllReviews">
                        {{ dt('providerDetail.reviews.seeAllReviewsCount', { count: reviewSummary?.review_count }) }}
                      </NButton>
                    </div>
                  </div>

                  <div v-else-if="!loadingReviews" class="empty-reviews">
                    <NEmpty :description="dt('providerDetail.reviews.noReviews')">
                      <template #extra>
                        <NButton type="primary" @click="navigateToReviewForm" v-if="isAuthenticated && !hasUserReviewed">
                          {{ dt('providerDetail.reviews.beFirstToReview') }}
                        </NButton>

                        <div v-if="!isAuthenticated" class="login-prompt">
                          {{ dt('providerDetail.reviews.loginToReview') }}
                        </div>
                      </template>
                    </NEmpty>
                  </div>
                </section>
              </NTabPane>

              <NTabPane name="office" :tab="dt('providerDetail.tabs.office')">
                <!-- Office Images -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.officeImages') }}</h2>

                  <div v-if="loadingImages" class="images-loading">
                    <NSpin size="small" />
                    <span>{{ dt('providerDetail.images.loading') }}</span>
                  </div>

                  <div v-else-if="facilityImages && facilityImages.length > 0" class="office-images">
                    <div class="facility-images-grid">
                      <div v-for="(image, index) in facilityImages" :key="image.id" class="facility-image-card" @click="() => window.open(image.presigned_url, '_blank')">
                        <img :src="image.presigned_url" :alt="dt('providerDetail.images.officeImageAlt', { index: index + 1 })" />
                        <div class="image-overlay">
                          <div class="image-type">{{ image.image_type || dt('providerDetail.images.office') }}</div>
                          <div class="image-description" v-if="image.description">{{ image.description }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="empty-section">
                    <NEmpty :description="dt('providerDetail.images.noOfficeImages')" />
                  </div>
                </section>
              </NTabPane>
            </NTabs>
          </div>

          <!-- Desktop View -->
          <div class="desktop-view">
            <div class="desktop-layout">
              <!-- Left Column -->
              <div class="left-column">
                <!-- Basic Information -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.basicInformation') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.city">
                      <div class="info-label">{{ dt('providerDetail.fields.location') }}:</div>
                      <div class="info-value">{{ provider.city }}</div>
                    </div>
                    <div class="info-item" v-if="provider.specializations?.length">
                      <div class="info-label">{{ dt('providerDetail.fields.practiceAreas') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="spec in provider.specializations" :key="spec" type="primary" class="spec-tag">
                          {{ spec }}
                        </NTag>
                      </div>
                    </div>
                    <div class="info-item full-width" v-if="provider.additional_info">
                      <div class="info-label">{{ dt('providerDetail.fields.about') }}:</div>
                      <div class="info-value">{{ provider.additional_info }}</div>
                    </div>
                    <div class="info-item" v-if="provider.tags?.length">
                      <div class="info-label">{{ dt('providerDetail.fields.tags') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="tag in provider.tags" :key="tag" type="success" class="tag-item">
                          {{ tag }}
                        </NTag>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Contact Information -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.contactInformation') }}</h2>
                  <div class="info-grid">
                    <!-- Phone Numbers -->
                    <div class="info-item full-width">
                      <div class="info-label">{{ dt('providerDetail.fields.phone') }}:</div>
                      <div class="info-value">
                        <div class="phone-section">
                          <div v-if="!showPhones">
                            <button class="phone-button" @click="loadPhoneNumbers" :disabled="loadingPhones">
                              <span v-if="loadingPhones" class="loading-dots">
                                {{ dt('providerDetail.phone.loading') }}<span>.</span><span>.</span><span>.</span>
                              </span>
                              <span v-else>
                                <span class="phone-icon">📞</span>
                                <span class="phone-text">{{ dt('providerDetail.phone.showPhoneNumbers') }}</span>
                              </span>
                            </button>
                          </div>

                          <div v-else-if="phoneNumbers.length > 0" class="phone-list">
                            <div v-for="(phone, index) in phoneNumbers" :key="index" class="phone-item">
                              <a :href="`tel:${phone}`" class="phone-link">
                                {{ phone }}
                              </a>
                            </div>
                          </div>

                          <div v-else-if="showPhones" class="no-phones">
                            {{ dt('providerDetail.phone.noPhoneNumbers') }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Email -->
                    <div class="info-item" v-if="provider.email">
                      <div class="info-label">{{ dt('providerDetail.fields.email') }}:</div>
                      <div class="info-value">
                        <a :href="`mailto:${provider.email}`" class="contact-link">
                          {{ provider.email }}
                        </a>
                      </div>
                    </div>

                    <!-- Website -->
                    <div class="info-item" v-if="provider.website_url">
                      <div class="info-label">{{ dt('providerDetail.fields.website') }}:</div>
                      <div class="info-value">
                        <a :href="provider.website_url" target="_blank" class="contact-link">
                          {{ provider.website_url }}
                        </a>
                      </div>
                    </div>

                    <!-- Address -->
                    <div class="info-item full-width" v-if="provider.address_line1 || provider.address_line2 || provider.address_line3">
                      <div class="info-label">{{ dt('providerDetail.fields.address') }}:</div>
                      <div class="info-value address-block">
                        <p v-if="provider.address_line1">{{ provider.address_line1 }}</p>
                        <p v-if="provider.address_line2">{{ provider.address_line2 }}</p>
                        <p v-if="provider.address_line3">{{ provider.address_line3 }}</p>
                        <p>
                          {{ provider.city }}{{ provider.city && provider.state ? ', ' : '' }}{{ provider.state }}
                          {{ provider.postal_code ? provider.postal_code : '' }}
                        </p>
                      </div>
                    </div>

                    <!-- Social Media -->
                    <div class="info-item full-width" v-if="provider.social_media && provider.social_media.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.socialMedia') }}:</div>
                      <div class="info-value">
                        <div class="social-links">
                          <div v-for="(social, index) in provider.social_media" :key="index" class="social-item">
                            <strong>{{ social.platform }}:</strong>
                            <a :href="social.url.startsWith('http') ? social.url : 'https://' + social.url" target="_blank" class="contact-link">
                              {{ social.url }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Professional Details -->
                <section v-if="showProfessionalDetails" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.professionalDetails') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.years_of_experience">
                      <div class="info-label">{{ dt('providerDetail.fields.yearsOfExperience') }}:</div>
                      <div class="info-value">{{ provider.years_of_experience }}</div>
                    </div>

                    <div class="info-item full-width" v-if="provider.services && provider.services.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.legalServices') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="service in provider.services" :key="service" type="info" class="service-tag">
                          {{ service }}
                        </NTag>
                      </div>
                    </div>

                    <div class="info-item full-width" v-if="provider.spoken_languages && provider.spoken_languages.length > 0">
                      <div class="info-label">{{ dt('providerDetail.fields.languages') }}:</div>
                      <div class="info-value tags-container">
                        <NTag v-for="lang in provider.spoken_languages" :key="lang" type="default" class="lang-tag">
                          {{ lang }}
                        </NTag>
                      </div>
                    </div>
                  </div>

                  <!-- Education -->
                  <div v-if="provider.education && provider.education.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.education') }}</h3>
                    <div class="education-list">
                      <div v-for="(edu, i) in provider.education" :key="i" class="education-item">
                        <div class="education-year">{{ edu.year }}</div>
                        <div class="education-details">
                          <div class="education-degree">{{ edu.degree }}</div>
                          <div class="education-institution">{{ edu.institution }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Certifications -->
                  <div v-if="provider.certifications && provider.certifications.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.certifications') }}</h3>
                    <div class="certification-list">
                      <div v-for="(cert, i) in provider.certifications" :key="i" class="certification-item">
                        <div class="certification-year">{{ cert.year }}</div>
                        <div class="certification-details">
                          <div class="certification-name">{{ cert.name }}</div>
                          <div class="certification-issuer">{{ cert.issuer }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Legal Practice Details -->
                <section v-if="showLegalPracticeDetails" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.legalPracticeDetails') }}</h2>

                  <div v-if="provider.court_representations && provider.court_representations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.courtRepresentations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="court in provider.court_representations" :key="court" type="primary" class="court-tag">
                        {{ court }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.case_types_handled && provider.case_types_handled.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.caseTypesHandled') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="caseType in provider.case_types_handled" :key="caseType" type="info" class="case-tag">
                        {{ caseType }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.pro_bono_services && provider.pro_bono_services.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.proBonoServices') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="service in provider.pro_bono_services" :key="service" type="success" class="pro-bono-tag">
                        {{ service }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.affiliated_organizations && provider.affiliated_organizations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.barAssociationsOrganizations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="org in provider.affiliated_organizations" :key="org" type="warning" class="org-tag">
                        {{ org }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.technology_available && provider.technology_available.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.legalTechnologyAvailable') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="tech in provider.technology_available" :key="tech" type="info" class="tech-tag">
                        {{ tech }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.awards_accreditations && provider.awards_accreditations.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.awardsAccreditations') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="award in provider.awards_accreditations" :key="award" type="success" class="award-tag">
                        {{ award }}
                      </NTag>
                    </div>
                  </div>

                  <div v-if="provider.provider_staff && provider.provider_staff.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.staffMembers') }}</h3>
                    <div class="staff-list">
                      <div v-for="(staff, i) in provider.provider_staff" :key="i" class="staff-item">
                        <div class="staff-name">{{ staff.name }}</div>
                        <div class="staff-role">{{ staff.role }}</div>
                        <div v-if="staff.qualifications" class="staff-qualifications">{{ staff.qualifications }}</div>
                      </div>
                    </div>
                  </div>

                  <div v-if="provider.services_offered && provider.services_offered.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.servicesOffered') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="service in provider.services_offered" :key="service" type="primary" class="detailed-service-tag">
                        {{ service }}
                      </NTag>
                    </div>
                  </div>
                </section>

                <!-- Practice Information -->
                <section v-if="showPracticeInfo" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.practiceInformation') }}</h2>
                  <div class="info-grid">
                    <div class="info-item" v-if="provider.accepts_new_clients !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.acceptingNewClients') }}:</div>
                      <div class="info-value">
                        <span :class="provider.accepts_new_clients ? 'status-positive' : 'status-negative'">
                          {{ provider.accepts_new_clients ? dt('providerDetail.common.yes') : dt('providerDetail.common.no') }}
                        </span>
                      </div>
                    </div>

                    <div class="info-item" v-if="provider.virtual_consultations !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.virtualConsultations') }}:</div>
                      <div class="info-value">
                        <span :class="provider.virtual_consultations ? 'status-positive' : 'status-negative'">
                          {{ provider.virtual_consultations ? dt('providerDetail.common.yes') : dt('providerDetail.common.no') }}
                        </span>
                      </div>
                    </div>

                    <div class="info-item" v-if="provider.average_response_time_minutes !== null">
                      <div class="info-label">{{ dt('providerDetail.fields.averageResponseTime') }}:</div>
                      <div class="info-value">{{ provider.average_response_time_minutes }} {{ dt('providerDetail.common.hours') }}</div>
                    </div>
                  </div>

                  <!-- Office Hours -->
                  <div v-if="provider.office_hours && Object.keys(provider.office_hours).length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.officeHours') }}</h3>
                    <div class="hours-container">
                      <div v-for="(hours, i) in formatOfficeHours(provider.office_hours)" :key="i" class="hours-item">
                        <div class="hours-day">{{ hours.day }}</div>
                        <div class="hours-time">{{ hours.open }} - {{ hours.close }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Parking Info -->
                  <div v-if="provider.parking_info" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.parkingInformation') }}</h3>
                    <div class="parking-info">{{ provider.parking_info }}</div>
                  </div>

                  <!-- Payment Methods -->
                  <div v-if="provider.payment_methods && provider.payment_methods.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.paymentMethods') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="method in provider.payment_methods" :key="method" type="warning" class="payment-tag">
                        {{ method }}
                      </NTag>
                    </div>
                  </div>

                  <!-- Accessibility Features -->
                  <div v-if="provider.accessibility_options && provider.accessibility_options.length > 0" class="subsection">
                    <h3 class="subsection-title">{{ dt('providerDetail.sections.accessibilityFeatures') }}</h3>
                    <div class="tags-container">
                      <NTag v-for="option in provider.accessibility_options" :key="option" type="success" class="access-tag">
                        {{ option }}
                      </NTag>
                    </div>
                  </div>
                </section>

                <!-- Additional Locations -->
                <section v-if="provider.additional_locations && provider.additional_locations.length > 0" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.additionalOfficeLocations') }}</h2>
                  <div class="locations-list">
                    <div v-for="(location, index) in provider.additional_locations" :key="index" class="location-item">
                      <div>{{ location.address }}</div>
                      <div>{{ location.city }}, {{ location.state }} {{ location.postal_code }}</div>
                    </div>
                  </div>
                </section>
              </div>

              <!-- Right Column -->
              <div class="right-column">
                <!-- Map Section -->
                <section v-if="hasLocation" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.location') }}</h2>
                  <div v-if="mapError" class="map-error">
                    <div class="error-content">
                      <p>{{ mapError }}</p>
                      <NButton size="small" @click="initializeMap">
                        {{ dt('providerDetail.map.retryLoading') }}
                      </NButton>
                    </div>
                  </div>
                  <div v-else class="map-container">
                    <div ref="mapContainer" class="map"></div>
                  </div>
                </section>

                <!-- Bookmark -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.bookmark') }}</h2>
                  <div v-if="isAuthenticated" class="bookmark-container">
                    <BookmarkButton :provider-id="route.params.id" :initial-status="null" @status-change="onBookmarkStatusChange" />
                  </div>
                  <div v-else class="login-prompt">
                    {{ dt('providerDetail.bookmark.loginToBookmark') }}
                  </div>
                </section>

                <!-- Ratings Summary -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.ratingsReviews') }}</h2>

                  <div v-if="loadingReviews" class="rating-loading">
                    <NSpin size="small" />
                    <span>{{ dt('providerDetail.reviews.loading') }}</span>
                  </div>

                  <div v-else-if="reviewSummary" class="rating-summary">
                    <div class="overall-rating">
                      <div class="rating-number">{{ reviewSummary.average_rating.toFixed(1) }}</div>
                      <NRate readonly :value="reviewSummary.average_rating" allow-half />
                      <div class="review-count">{{ reviewSummary.review_count }} {{ dt('providerDetail.reviews.reviewsCount') }}</div>
                    </div>

                    <div class="detailed-ratings" v-if="reviewSummary.review_count > 0">
                      <div class="rating-item" v-if="reviewSummary.average_communication">
                        <span class="rating-label">{{ dt('providerDetail.reviews.communication') }}:</span>
                        <NRate readonly :value="reviewSummary.average_communication" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_communication.toFixed(1) }}</span>
                      </div>

                      <div class="rating-item" v-if="reviewSummary.average_knowledge">
                        <span class="rating-label">{{ dt('providerDetail.reviews.knowledge') }}:</span>
                        <NRate readonly :value="reviewSummary.average_knowledge" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_knowledge.toFixed(1) }}</span>
                      </div>

                      <div class="rating-item" v-if="reviewSummary.average_timeliness">
                        <span class="rating-label">{{ dt('providerDetail.reviews.timeliness') }}:</span>
                        <NRate readonly :value="reviewSummary.average_timeliness" allow-half size="small" />
                        <span class="rating-value">{{ reviewSummary.average_timeliness.toFixed(1) }}</span>
                      </div>
                    </div>

                    <div class="review-actions">
                      <NButton type="info" @click="navigateToAllReviews" v-if="reviewSummary.review_count > 0">
                        {{ dt('providerDetail.reviews.seeAllReviews') }}
                      </NButton>

                      <NButton @click="navigateToReviewUpdateForm" v-if="isAuthenticated && hasUserReviewed">
                        {{ dt('providerDetail.reviews.editYourReview') }}
                      </NButton>

                      <div v-if="!isAuthenticated" class="login-prompt">
                        {{ dt('providerDetail.reviews.loginToReview') }}
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Recent Reviews -->
                <section v-if="recentReviews.length > 0" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.reviews.recentReviews') }}</h2>

                  <div class="recent-reviews">
                    <div v-for="(review, index) in recentReviews" :key="index" class="review-item">
                      <div class="review-header">
                        <div class="reviewer-info">
                          <div class="reviewer-name">{{ review.reviewer_name || dt('providerDetail.reviews.anonymous') }}</div>
                          <div class="review-date">{{ formatDate(review.created_at) }}</div>
                        </div>
                        <NRate readonly :value="review.rating" size="small" />
                      </div>

                      <div class="review-text" v-if="review.review_text">
                        <span class="truncated-text">{{ review.review_text }}</span>
                      </div>

                      <NDivider v-if="index < recentReviews.length - 1" />
                    </div>

                    <div class="see-all-reviews">
                      <NButton text @click="navigateToAllReviews">
                        {{ dt('providerDetail.reviews.seeAllReviewsCount', { count: reviewSummary?.review_count }) }}
                      </NButton>
                    </div>
                  </div>
                </section>

                <!-- Client Types -->
                <section v-if="provider.client_types && provider.client_types.length > 0" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.clientTypesServed') }}</h2>
                  <div class="tags-container">
                    <NTag v-for="clientType in provider.client_types" :key="clientType" type="warning" class="client-tag">
                      {{ clientType }}
                    </NTag>
                  </div>
                </section>

                <!-- Office Images -->
                <section v-if="facilityImages && facilityImages.length > 0" class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.officeImages') }}</h2>
                  <div class="facility-images-grid">
                    <div v-for="(image, index) in facilityImages" :key="image.id" class="facility-image-card" @click="() => window.open(image.presigned_url, '_blank')">
                      <img :src="image.presigned_url" :alt="dt('providerDetail.images.officeImageAlt', { index: index + 1 })" />
                      <div class="image-overlay">
                        <div class="image-type">{{ image.image_type || dt('providerDetail.images.office') }}</div>
                        <div class="image-description" v-if="image.description">{{ image.description }}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Statistics -->
                <section class="info-section">
                  <h2 class="section-title">{{ dt('providerDetail.sections.statistics') }}</h2>
                  <div class="stats-grid">
                    <div class="stat-item">
                      <div class="stat-label">{{ dt('providerDetail.stats.totalViews') }}:</div>
                      <div class="stat-value">{{ provider.views || 0 }}</div>
                    </div>
                    <div class="stat-item" v-if="provider.created_at">
                      <div class="stat-label">{{ dt('providerDetail.stats.profileCreated') }}:</div>
                      <div class="stat-value">{{ formatDate(provider.created_at) }}</div>
                    </div>
                    <div class="stat-item" v-if="provider.updated_at">
                      <div class="stat-label">{{ dt('providerDetail.stats.lastUpdated') }}:</div>
                      <div class="stat-value">{{ formatDate(provider.updated_at) }}</div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        <!-- Provider Not Found -->
        <div v-else class="not-found">
          <h2>{{ dt('providerDetail.notFound.title') }}</h2>
          <p>{{ dt('providerDetail.notFound.description') }}</p>
          <NButton type="primary" @click="goToProviders">
            {{ dt('providerDetail.notFound.viewAllProviders') }}
          </NButton>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.provider-detail-page {
  min-height: 100vh;
  background-color: #f9fafb;
  padding-bottom: 2rem;
}

.main-content {
  padding-top: 1rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.back-section {
  margin-bottom: 1.5rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}

.loading-section,
.error-section,
.not-found {
  text-align: center;
  padding: 3rem 0;
}

.loading-text {
  margin-top: 1rem;
  color: #6b7280;
}

.provider-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.provider-main {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.profile-avatar {
  width: 100px !important;
  height: 100px !important;
  cursor: pointer;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.provider-info {
  flex: 1;
  min-width: 0;
}

.provider-name {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.provider-type {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 12px;
  font-weight: 500;
}

.provider-status {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

.provider-username {
  color: #888;
  font-size: 0.875rem;
  font-style: italic;
}

@media (max-width: 768px) {
  .provider-header {
    padding: 16px;
  }
  
  .provider-main {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }
  
  .provider-name {
    font-size: 1.5rem;
  }
}

.status-unclaimed {
  background-color: #f0f0f0;
  color: #666;
}

.status-claimed,
.status-verified {
  background-color: #e6f7ea;
  color: #2e7d32;
}

.status-suspended {
  background-color: #ffebee;
  color: #c62828;
}

.status-unverified,
.status-draft {
  background-color: #fff8e1;
  color: #f57c00;
}

.claim-notice {
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.notice-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.notice-text {
  margin-bottom: 1rem;
  line-height: 1.5;
}

.map-container,
.map-container-mobile {
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.map-container-mobile {
  height: 250px;
}

.map {
  height: 100%;
  width: 100%;
  min-height: 250px;
}

.map-error {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.error-content {
  text-align: center;
  color: #ef4444;
}

.error-content p {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

:global(.leaflet-container) {
  width: 100% !important;
  height: 100% !important;
  min-height: 250px !important;
}

:global(.provider-marker) {
  background: transparent !important;
  border: none !important;
}

.mobile-tabs {
  display: none;
}

.desktop-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}

.info-section {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.subsection {
  margin-top: 1.5rem;
}

.subsection-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 0.375rem;
}

.info-value {
  color: #1f2937;
  line-height: 1.5;
}

.address-block p {
  margin: 0;
  line-height: 1.5;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.spec-tag,
.tag-item,
.service-tag,
.lang-tag,
.payment-tag,
.access-tag,
.client-tag,
.court-tag,
.case-tag,
.pro-bono-tag,
.org-tag,
.tech-tag,
.award-tag,
.detailed-service-tag {
  margin-bottom: 0.5rem;
}

.contact-link {
  color: #3b82f6;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.phone-section {
  width: 100%;
}

.phone-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
  transition: all 0.2s ease;
}

.phone-button:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

.phone-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.phone-icon {
  font-size: 1.1rem;
}

.phone-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phone-item {
  padding: 6px 12px;
  background-color: #f9fafb;
  border-radius: 4px;
  border-left: 3px solid #3b82f6;
}

.phone-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.phone-link:hover {
  text-decoration: underline;
}

.loading-dots span {
  animation: dots 1.5s infinite;
  animation-fill-mode: both;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.5s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dots {
  0%,
  80%,
  100% {
    opacity: 0;
  }

  40% {
    opacity: 1;
  }
}

.no-phones {
  color: #6b7280;
  font-style: italic;
  padding: 8px 0;
}

.login-prompt {
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
  padding: 1rem 0;
}

.login-prompt a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.login-prompt a:hover {
  text-decoration: underline;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.social-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.education-list,
.certification-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.education-item,
.certification-item {
  display: flex;
  gap: 1rem;
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 6px;
}

.education-year,
.certification-year {
  font-weight: 600;
  color: #4b5563;
  min-width: 60px;
}

.education-details,
.certification-details {
  flex: 1;
}

.education-degree,
.certification-name {
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.education-institution,
.certification-issuer {
  font-size: 0.9rem;
  color: #4b5563;
}

.staff-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.staff-item {
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.staff-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.staff-role {
  color: #4b5563;
  margin-bottom: 0.25rem;
}

.staff-qualifications {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

.hours-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.hours-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background-color: #f9fafb;
  border-radius: 4px;
}

.hours-day {
  font-weight: 500;
  color: #4b5563;
}

.hours-time {
  color: #1f2937;
}

.parking-info {
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 6px;
  line-height: 1.5;
}

.status-positive {
  color: #10b981;
  font-weight: 500;
}

.status-negative {
  color: #ef4444;
  font-weight: 500;
}

.locations-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.location-item {
  background-color: #f9fafb;
  border-left: 3px solid #10b981;
  padding: 0.75rem;
  border-radius: 4px;
  line-height: 1.5;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background-color: #f9fafb;
  border-radius: 4px;
}

.stat-label {
  font-weight: 500;
  color: #4b5563;
}

.stat-value {
  color: #1f2937;
  font-weight: 500;
}

.rating-summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.overall-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0.5rem;
}

.rating-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.review-count {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.detailed-ratings {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rating-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.rating-label {
  min-width: 100px;
  color: #4b5563;
}

.rating-value {
  margin-left: 0.5rem;
  color: #4b5563;
  font-weight: 500;
}

.review-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.rating-loading,
.images-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #6b7280;
}

.recent-reviews {
  display: flex;
  flex-direction: column;
}

.review-item {
  padding: 0.75rem 0;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.reviewer-info {
  display: flex;
  flex-direction: column;
}

.reviewer-name {
  font-weight: 500;
  color: #111827;
}

.review-date {
  font-size: 0.75rem;
  color: #6b7280;
}

.review-text {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #4b5563;
  margin-top: 0.5rem;
}

.truncated-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.see-all-reviews {
  text-align: right;
  margin-top: 0.5rem;
}

.facility-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.facility-image-card {
  position: relative;
  height: 110px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.facility-image-card:hover {
  transform: scale(1.03);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.facility-image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 6px 10px;
  font-size: 0.75rem;
}

.image-type {
  font-weight: 600;
  margin-bottom: 2px;
}

.image-description {
  font-size: 0.7rem;
  opacity: 0.9;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.empty-section {
  padding: 2rem 0;
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }

  .mobile-tabs {
    display: block;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .provider-header h1 {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .subsection-title {
    font-size: 1rem;
  }

  .phone-button {
    width: 100%;
    justify-content: center;
  }

  .map-container-mobile {
    height: 200px;
  }

  .map-error {
    height: 200px;
  }
}
</style>